<template>
  <ReportWrapper>
    <template #actions>
      <div class="flex gap-x-2">
        <CfsDateField
          v-model="selectedDateRange"
          label="Date Range"
          placeholder="Select date range"
          range
          class="w-2/6 x-sm:w-full"
          :rules="[rules.required]"
        />
        <CfsSelect
          :value="selectedReportBy"
          :items="reportByOptions"
          label="Report By"
          class="w-2/6 x-sm:w-full"
          hide-details
          item-text="text"
          item-value="value"
          :return-object="false"
          @input="selectedReportBy = $event"
        />
      </div>

      <v-btn color="primary" :loading="isLoading" :disabled="!selectedDateRange" @click="download">
        <v-icon size="1rem" class="mr-1">mdi-tray-arrow-down</v-icon>
        <span>Download</span>
      </v-btn>
    </template>
  </ReportWrapper>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useReports } from '@/composables/reports/useReports';
import { downloadFile } from '@/helpers/download';
import { useToast } from '@/composables/useToast';
import ReportWrapper from '@/components/reports/ReportWrapper.vue';
import rules from '@/helpers/rules';

export default defineComponent({
  name: 'SalesJournalReport',
  components: {
    ReportWrapper,
  },
  setup() {
    const toast = useToast();

    const selectedDateRange = ref<{ start: string; end: string } | null>(null);
    const selectedReportBy = ref<'customer' | 'code' | 'all'>('all');

    const reportByOptions = [
      { text: 'By Customer', value: 'customer' },
      { text: 'By Code', value: 'code' },
      { text: 'All', value: 'all' },
    ];

    const { useDownloadSalesJournalReport } = useReports();
    const { mutateAsync: downloadReport, isLoading } = useDownloadSalesJournalReport();

    const formatDateForFilename = (dateString: string) => {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    };

    const download = async () => {
      if (!selectedDateRange.value) {
        return toast.info('Please select date range.');
      }

      try {
        const response = await downloadReport({
          dateRange: {
            start: new Date(selectedDateRange.value.start).toISOString(),
            end: new Date(selectedDateRange.value.end).toISOString(),
          },
          reportBy: selectedReportBy.value,
        });

        if (!response?.base64Data) {
          return toast.error('No data received from server');
        }

        const startDate = formatDateForFilename(selectedDateRange.value.start);
        const endDate = formatDateForFilename(selectedDateRange.value.end);
        const reportType =
          reportByOptions.find((option) => option.value === selectedReportBy.value)?.text || 'All';
        const filename = `Sales_Journal_${reportType.replace(
          ' ',
          '_'
        )}_${startDate}_to_${endDate}.xlsx`;

        downloadFile(response.base64Data, filename);
        toast.success('Report downloaded successfully');
      } catch (error) {
        // Error is already handled by the composable's onError callback
        // No need to show another error message
      }
    };

    return {
      selectedDateRange,
      selectedReportBy,
      reportByOptions,
      isLoading,
      download,
      rules,
    };
  },
});
</script>
